import { useCallback, useEffect, useState } from "react";
import { localStorageHelper } from "./localStorageHelper";
import {
  IDLE_TIMEOUT_MS,
  IDLE_WARNING_MS,
  LAST_ACTIVITY_KEY,
  PROFILE_KEY,
} from "../utils/Constants";

const ACTIVITY_EVENTS = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "wheel"];
// Limits localStorage writes while the user is moving the mouse.
const ACTIVITY_WRITE_THROTTLE_MS = 5 * 1000;
const CHECK_INTERVAL_MS = 1000;

/**
 * Logs the user out after IDLE_TIMEOUT_MS without activity in any open tab.
 * Last activity is kept in localStorage so all tabs share one idle clock.
 */
export const useIdleLogout = () => {
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);

  const markActive = useCallback(() => {
    localStorageHelper.store(LAST_ACTIVITY_KEY, Date.now());
  }, []);

  useEffect(() => {
    const logout = () => {
      localStorageHelper.clearSession();
      window.location.reload();
    };

    const lastActivity = localStorageHelper.load(LAST_ACTIVITY_KEY);
    if (typeof lastActivity === "number" && Date.now() - lastActivity >= IDLE_TIMEOUT_MS) {
      logout();
      return;
    }
    markActive();

    let lastWrite = Date.now();
    const onActivity = () => {
      if (Date.now() - lastWrite < ACTIVITY_WRITE_THROTTLE_MS) return;
      lastWrite = Date.now();
      markActive();
    };

    const check = () => {
      const last = localStorageHelper.load(LAST_ACTIVITY_KEY) ?? Date.now();
      const remaining = IDLE_TIMEOUT_MS - (Date.now() - last);
      if (remaining <= 0) {
        logout();
      } else if (remaining <= IDLE_WARNING_MS) {
        setSecondsLeft(Math.ceil(remaining / 1000));
      } else {
        setSecondsLeft(null);
      }
    };

    // Logging out in another tab removes the profile; follow it here.
    const onStorage = (e: StorageEvent) => {
      if (e.key === PROFILE_KEY && e.newValue === null) {
        window.location.reload();
      }
    };

    ACTIVITY_EVENTS.forEach((event) =>
      window.addEventListener(event, onActivity, { passive: true })
    );
    window.addEventListener("storage", onStorage);
    const interval = window.setInterval(check, CHECK_INTERVAL_MS);

    return () => {
      ACTIVITY_EVENTS.forEach((event) => window.removeEventListener(event, onActivity));
      window.removeEventListener("storage", onStorage);
      window.clearInterval(interval);
    };
  }, [markActive]);

  return {
    showWarning: secondsLeft !== null,
    secondsLeft: secondsLeft ?? 0,
    stayLoggedIn: markActive,
  };
};
